<template>
  <div class="settings-nav">
    <div class="header-content" v-if="user">
      <transition name="fadeDown">
        <v-avatar class="avatar" size="146px">
          <v-img
            v-if="profileImage && profileImage.imageURL"
            alt="User Profile Image"
            class="elevation-6"
            :src="`${profileImage.imageURL}?w=200&h=200&fit=crop`"
            :key="user.dateCreated"
            :lazy-src="$options.lazyProfileImage"
          >
            <template v-slot:placeholder>
              <v-layout fill-height align-center justify-center ma-0>
                <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
              </v-layout>
            </template>
          </v-img>
        </v-avatar>
      </transition>
    </div>

    <div class="sidebar-nav text-center">
      <v-flex xs12 v-if="user">
        <h2
          :class="`headline font-weight-bold
          ${$vuetify.theme.dark ? 'white--text' : 'grey--text text--darken-2'}`"
        >
          {{ user.displayName }}
        </h2>
      </v-flex>
      <v-flex xs12 v-if="user">
        <span :class="`${$vuetify.theme.dark ? 'white--text' : 'grey--text'}`">
          {{ user.email }}
        </span>
        <div
          v-if="user && user.username"
          :class="`${$vuetify.theme.dark ? 'white--text' : 'grey--text'}`"
        >
          {{ user.username }}
        </div>
      </v-flex>

      <v-flex xs12 class="px-4">
        <router-link
          :to="{
            props: true,
            name: 'editProfile',
          }"
        >
          <v-btn
            block
            color="primary"
            :class="['custom-button', $vuetify.theme.dark ? 'lighten-1' : '']"
          >
            {{ $t('settingsNav.editProfile') }}
          </v-btn>
        </router-link>
      </v-flex>

      <v-flex xs12>
        <v-divider class="border-color"></v-divider>
      </v-flex>

      <v-flex xs12 class="px-4">
        <router-link
          :to="{
            props: true,
            name: 'shop',
          }"
        >
          <v-btn
            block
            :color="`${$vuetify.theme.dark ? '' : 'secondary'}`"
            :class="`custom-button ${$vuetify.theme.dark ? 'custom-button-dark' : ''}`"
          >
            {{ $t('settingsNav.purchaseLicense') }}
          </v-btn>
        </router-link>

        <v-btn
          :color="`${$vuetify.theme.dark ? '' : 'secondary'}`"
          :loading="loadingClick"
          :disabled="loadingClick"
          :class="`custom-button ${$vuetify.theme.dark ? 'custom-button-dark' : ''}`"
          @click="manageBilling($event)"
          block
        >
          {{ $t('settingsNav.manageBilling') }}
        </v-btn>

        <ProductKey minimal :buttonText="$t('store.productCode.redeemCode')" />
      </v-flex>

      <v-flex xs12>
        <v-divider class="border-color"></v-divider>
      </v-flex>

      <v-flex xs12 v-if="limitedProducts">
        <h3
          :class="`headline font-weight-bold pt-6 pb-6
          ${$vuetify.theme.dark ? 'white--text' : 'grey--text text--darken-2'}`"
        >
          {{ $t('store.general.promotions') }}
        </h3>

        <v-flex v-for="product in limitedProducts" :key="product.productID" xs12>
          <Product
            :productId="product.productID.toString()"
            :product="product"
            :loading="loadingProducts"
            promotion
          />
        </v-flex>
      </v-flex>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ProductKey from '@/components/ProductKey';
import Product from '@/components/Product';
import { lazyProfileImage } from '@/lib/constants';

export default {
  name: 'settings-nav',
  props: ['user', 'loading'],

  components: {
    ProductKey,
    Product,
  },

  data() {
    return {
      loadingClick: false,
    };
  },

  created() {
    //* non-reactive constants
    this.$options.lazyProfileImage = lazyProfileImage;
  },

  mounted() {
    const payload = { promotion: true };
    this.$store.dispatch('shop/list', payload);
  },

  computed: {
    ...mapGetters({
      productsList: 'shop/list',
      loadingProducts: 'shop/loading',
      canViewAdmin: 'users/canViewAdmin',
    }),

    limitedProducts() {
      if (!this.productsList?.length) {
        return;
      }
      return this.productsList.slice(0, 3);
    },

    profileImage() {
      return this.user?.profileImage ? this.user?.profileImage : '';
    },
  },

  methods: {
    manageBilling(e) {
      this.loadingClick = true;
      e.preventDefault();
      const url = window.location.href;
      const payload = {
        return_url: url,
      };
      this.$store
        .dispatch('shop/manageBilling', payload)
        .then((res) => {
          window.open(`${res.billing_portal_url}`, '_self');
          this.loadingClick = false;
        })
        .catch((err) => {
          console.error('err:', err);
          try {
            const {
              data: { error = {} },
            } = err;

            this.$root.vtoast.show({
              text: `${error.code}: ${error.msg.replace('stripe', '')}`,
              color: 'info',
            });
            this.loadingClick = false;
          } catch (error) {
            this.$root.vtoast.show({
              text: `No billing customer found.`,
              color: 'info',
            });
            this.loadingClick = false;
          }
        });
    },
  },
};
</script>

<style scoped>
.header {
  font-size: 16px;
}

.avatar {
  position: absolute;
  top: -65px;
  left: 85px;
}

.avatar img {
  object-fit: contain;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.5);
  border: solid 3px #ffffff;
  border: solid 3px var(--white);
}

.sidebar-nav {
  padding-top: 100px;
  padding-bottom: 20px;
}

.theme--light .sidebar-nav {
  background-color: var(--darkWhite);
}

.custom-button {
  height: 64px !important;
  /* width: 250px; */ /** This was originally added with container width */
  margin: 20px auto;
  font-size: 1rem !important;
  font-weight: 400;
}

.custom-button-dark {
  background-color: #6d6d6d !important;
  background-color: var(--grey) !important;
}

.custom-button:nth-child(2) {
  margin-bottom: 10px;
}

.link-light {
  color: #ba2127;
  font-size: 14px;
}

.link-dark {
  color: #ffffff8c;
  font-size: 14px;
}

.active-link-light {
  color: #212121;
  font-weight: 500;
  font-size: 14px;
}

.active-link-dark {
  color: #fff;
  font-weight: 500;
  font-size: 14px;
}

@media (max-width: 1280px) {
  .avatar {
    left: 50px;
  }

  /** This was originally added with container width */
  /* .custom-button {
    width: 200px;
  } */
}

@media (max-width: 1265px) {
  .avatar {
    left: 85px;
  }

  /** This was originally added with container width */
  /* .custom-button {
    width: 250px;
  } */
}

@media (max-width: 960px) {
  .avatar {
    left: 45px;
  }

  .custom-button {
    /* width: 170px; */ /** This was originally added with container width */
    font-size: 16px;
  }
}

@media (max-width: 600px) {
  .avatar {
    left: 90px;
  }

  /** This was originally added with container width */
  /* .custom-button {
    width: 90%;
  } */

  .theme--light .sidebar-nav,
  .theme--dark .sidebar-nav {
    padding-bottom: 30px;
  }
}
</style>
